import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

interface UnsavedChangesContextType {
  hasUnsavedChanges: boolean;
  setUnsavedSource: (sourceId: string, isDirty: boolean) => void;
}

const UnsavedChangesContext = createContext<
  UnsavedChangesContextType | undefined
>(undefined);

interface Props {
  children: ReactNode;
}

export const UnsavedChangesProvider = ({ children }: Props) => {
  const [dirtySourceIds, setDirtySourceIds] = useState<Set<string>>(
    () => new Set(),
  );

  const setUnsavedSource = useCallback((sourceId: string, isDirty: boolean) => {
    setDirtySourceIds((previousSourceIds) => {
      const sourceExists = previousSourceIds.has(sourceId);

      if (isDirty && sourceExists) return previousSourceIds;
      if (!isDirty && !sourceExists) return previousSourceIds;

      const nextSourceIds = new Set(previousSourceIds);

      if (isDirty) {
        nextSourceIds.add(sourceId);
      } else {
        nextSourceIds.delete(sourceId);
      }

      return nextSourceIds;
    });
  }, []);

  const value = useMemo(
    () => ({
      hasUnsavedChanges: dirtySourceIds.size > 0,
      setUnsavedSource,
    }),
    [dirtySourceIds.size, setUnsavedSource],
  );

  return (
    <UnsavedChangesContext.Provider value={value}>
      {children}
    </UnsavedChangesContext.Provider>
  );
};

export const useUnsavedChanges = () => {
  const context = useContext(UnsavedChangesContext);

  if (context === undefined) {
    throw new Error(
      "useUnsavedChanges must be used within an UnsavedChangesProvider",
    );
  }

  return { hasUnsavedChanges: context.hasUnsavedChanges };
};

export const useHasUnsavedChanges = () =>
  useContext(UnsavedChangesContext)?.hasUnsavedChanges ?? false;

export const useRegisterUnsavedChanges = (
  sourceId: string,
  isDirty: boolean,
) => {
  const context = useContext(UnsavedChangesContext);

  useEffect(() => {
    if (context === undefined) {
      return;
    }

    context.setUnsavedSource(sourceId, isDirty);

    return () => {
      context.setUnsavedSource(sourceId, false);
    };
  }, [context, isDirty, sourceId]);
};
